import { useMemo, useState } from "react";
import { Badge } from "flowbite-react";
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineCash,
  HiOutlineCalendar,
  HiOutlineCollection,
  HiOutlineDocumentText,
  HiOutlineLocationMarker,
  HiOutlineOfficeBuilding,
} from "react-icons/hi";
import { formatToReal } from "../../../utils/utils";
import TitleStripe from "../TitleStripe";
import NoOcurrence from "./NoOcurrence";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
};

const firstFilled = (...values) =>
  values.find(
    (value) =>
      value !== undefined && value !== null && String(value).trim() !== "",
  );

const parseCurrency = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return 0;

  const clean = value.replace(/[^\d.,-]/g, "").trim();
  if (!clean) return 0;

  const normalized =
    clean.includes(",") && clean.includes(".")
      ? clean.replace(/\./g, "").replace(",", ".")
      : clean.replace(",", ".");

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "R$ -";
  return formatToReal(value);
};

const normalizeStructuredData = (payload, quantidadeTotal) => {
  const estados = toArray(payload?.protestos).map((ufData, ufIndex) => {
    const cartorios = toArray(ufData?.cartorios).map(
      (cartorio, cartorioIndex) => {
        const titulos = toArray(cartorio?.titulos).map(
          (titulo, tituloIndex) => ({
            id: `${ufIndex}-${cartorioIndex}-${tituloIndex}`,
            data: firstFilled(titulo?.dataProtesto, titulo?.data, titulo?.DATA),
            valor: firstFilled(
              titulo?.valorProtestado,
              titulo?.valor,
              titulo?.VALOR,
            ),
          }),
        );

        return {
          id: `${ufIndex}-${cartorioIndex}`,
          cidade: firstFilled(cartorio?.cidade, cartorio?.CIDADE, "-"),
          codigoCidade: firstFilled(
            cartorio?.codigoCidade,
            cartorio?.CODIGO_CIDADE,
          ),
          nome: firstFilled(
            cartorio?.cartorio,
            cartorio?.nomeCartorio,
            cartorio?.CARTORIO,
          ),
          numeroProtestos:
            Number(firstFilled(cartorio?.numeroProtestos, titulos.length)) || 0,
          valorTotal: firstFilled(
            cartorio?.valorTotalProtestosCartorio,
            cartorio?.valorTotal,
            cartorio?.valor,
          ),
          titulos,
        };
      },
    );

    const numeroProtestos =
      Number(
        firstFilled(
          ufData?.numeroTotalProtestosUF,
          cartorios.reduce(
            (sum, cartorio) => sum + cartorio.numeroProtestos,
            0,
          ),
        ),
      ) || 0;

    return {
      id: `${ufData?.estado || ufData?.uf || "uf"}-${ufIndex}`,
      estado: firstFilled(ufData?.estado, ufData?.uf, "-"),
      numeroProtestos,
      valorTotal: firstFilled(
        ufData?.valorTotalProtestosEstado,
        ufData?.valorTotal,
      ),
      cartorios,
    };
  });

  const totalCartorios = estados.reduce(
    (sum, estado) => sum + estado.cartorios.length,
    0,
  );
  const totalTitulos = estados.reduce(
    (sum, estado) =>
      sum +
      estado.cartorios.reduce(
        (cartorioSum, cartorio) => cartorioSum + cartorio.titulos.length,
        0,
      ),
    0,
  );
  const totalValor = estados.reduce(
    (sum, estado) => sum + parseCurrency(estado.valorTotal),
    0,
  );
  const valorTotalProtestos = firstFilled(
    payload?.valorTotalProtestos,
    totalValor,
  );

  return {
    estados,
    providerId: payload?.providerId,
    provedorReserva: payload?.provedorReserva,
    totalCartorios,
    totalEstados: estados.length,
    totalTitulos,
    totalProtestos:
      Number(firstFilled(payload?.numeroTotalProtestos, quantidadeTotal)) ||
      estados.reduce((sum, estado) => sum + estado.numeroProtestos, 0),
    totalValor: valorTotalProtestos,
  };
};

const normalizeLegacyData = (rows, quantidadeTotal) => {
  const estadosMap = new Map();

  rows.forEach((row, index) => {
    const estado = firstFilled(row?.estado, row?.uf, row?.UF, "-");
    const cidade = firstFilled(row?.cidade, row?.CIDADE, "-");
    const cartorioNome = firstFilled(
      row?.cartorio,
      row?.nomeCartorio,
      row?.CARTORIO,
    );
    const codigoCidade = firstFilled(row?.codigoCidade, row?.CODIGO_CIDADE);
    const key = `${estado}|${cidade}|${cartorioNome || ""}|${
      codigoCidade || ""
    }`;

    if (!estadosMap.has(estado)) {
      estadosMap.set(estado, {
        id: `legacy-${estado}`,
        estado,
        numeroProtestos: 0,
        valorTotal: 0,
        cartoriosMap: new Map(),
      });
    }

    const estadoGroup = estadosMap.get(estado);

    if (!estadoGroup.cartoriosMap.has(key)) {
      estadoGroup.cartoriosMap.set(key, {
        id: `legacy-${index}`,
        cidade,
        codigoCidade,
        nome: cartorioNome,
        numeroProtestos: 0,
        valorTotal: 0,
        titulos: [],
      });
    }

    const cartorio = estadoGroup.cartoriosMap.get(key);
    const valor = firstFilled(row?.valor, row?.valorProtestado, row?.VALOR);
    const data = firstFilled(row?.data, row?.dataProtesto, row?.DATA);

    cartorio.numeroProtestos += 1;
    cartorio.valorTotal += parseCurrency(valor);
    cartorio.titulos.push({
      id: `legacy-title-${index}`,
      data,
      valor,
    });

    estadoGroup.numeroProtestos += 1;
    estadoGroup.valorTotal += parseCurrency(valor);
  });

  const estados = Array.from(estadosMap.values()).map((estado) => ({
    ...estado,
    cartorios: Array.from(estado.cartoriosMap.values()),
    cartoriosMap: undefined,
  }));

  const totalValor = estados.reduce(
    (sum, estado) => sum + parseCurrency(estado.valorTotal),
    0,
  );
  const totalCartorios = estados.reduce(
    (sum, estado) => sum + estado.cartorios.length,
    0,
  );
  const totalTitulos = rows.length;

  return {
    estados,
    providerId: undefined,
    provedorReserva: undefined,
    totalCartorios,
    totalEstados: estados.length,
    totalTitulos,
    totalProtestos: Number(quantidadeTotal) || totalTitulos,
    totalValor,
  };
};

const SummaryItem = ({ icon, label, value, hint }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </div>
        <div className="mt-1 break-words text-xl font-bold text-slate-950">
          {value}
        </div>
        {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
      </div>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-700">
        {icon}
      </div>
    </div>
  </div>
);

const LIMITE_TITULOS = 5;

const TitulosCartorio = ({ titulos }) => {
  const [expandido, setExpandido] = useState(false);
  const total = titulos.length;
  const restantes = Math.max(total - LIMITE_TITULOS, 0);

  const listaExibida = useMemo(
    () => (expandido ? titulos : titulos.slice(0, LIMITE_TITULOS)),
    [expandido, titulos],
  );

  if (total === 0) {
    return (
      <div className="px-3 py-3 text-sm text-slate-500">
        Nenhum título detalhado foi informado para este cartório.
      </div>
    );
  }

  return (
    <>
      <div className="divide-y divide-slate-100">
        {listaExibida.map((titulo) => (
          <div
            key={titulo.id}
            className="grid grid-cols-1 gap-2 px-3 py-3 text-sm sm:grid-cols-[1fr_180px] sm:items-center"
          >
            <div className="flex min-w-0 items-center gap-2 text-slate-700">
              <HiOutlineCalendar className="shrink-0 text-base text-slate-400 sm:hidden" />
              <span className="font-medium">
                {titulo.data || "Data não divulgada"}
              </span>
            </div>
            <div className="flex items-center gap-2 font-semibold text-red-700 sm:justify-end">
              <HiOutlineCash className="shrink-0 text-base text-red-600 sm:hidden" />
              {formatCurrency(titulo.valor)}
            </div>
          </div>
        ))}
      </div>

      {total > LIMITE_TITULOS && (
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-3 py-2">
          <button
            type="button"
            onClick={() => setExpandido((v) => !v)}
            aria-expanded={expandido}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            {expandido ? (
              <>
                <HiChevronUp className="text-base" />
                Ver menos
              </>
            ) : (
              <>
                <HiChevronDown className="text-base" />
                Ver mais
              </>
            )}
          </button>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Badge color="gray">
              Exibindo {Math.min(LIMITE_TITULOS, total)} de {total}
            </Badge>
            {!expandido && restantes > 0 && (
              <span className="text-slate-500">(+{restantes} restantes)</span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const ProtestosDirCartorio = ({ protestosDirCartorio, quantidadeTotal }) => {
  const normalized = useMemo(() => {
    if (
      protestosDirCartorio &&
      !Array.isArray(protestosDirCartorio) &&
      Array.isArray(protestosDirCartorio?.protestos)
    ) {
      return normalizeStructuredData(protestosDirCartorio, quantidadeTotal);
    }

    return normalizeLegacyData(toArray(protestosDirCartorio), quantidadeTotal);
  }, [protestosDirCartorio, quantidadeTotal]);

  const hasData =
    normalized.totalProtestos > 0 ||
    normalized.totalCartorios > 0 ||
    normalized.totalTitulos > 0;

  if (!hasData) {
    return (
      <div className="space-y-4">
        <TitleStripe>Protesto Nacional</TitleStripe>
        <NoOcurrence />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <TitleStripe>Protesto Nacional</TitleStripe>
      <div className="space-y-5">
        <div className="grid gap-3 grid-cols-4">
          <SummaryItem
            icon={<HiOutlineDocumentText className="text-xl" />}
            label="Total de protestos"
            value={normalized.totalProtestos}
            hint={`${normalized.totalTitulos} títulos listados`}
          />
          <SummaryItem
            icon={<HiOutlineCash className="text-xl" />}
            label="Valor total"
            value={formatCurrency(normalized.totalValor)}
            hint="Valor total protestado"
          />
          <SummaryItem
            icon={<HiOutlineCollection className="text-xl" />}
            label="Estados"
            value={normalized.totalEstados}
            hint="Estados com ocorrência"
          />
          <SummaryItem
            icon={<HiOutlineOfficeBuilding className="text-xl" />}
            label="Cidades"
            value={normalized.totalCartorios}
            hint="Agrupadas no retorno"
          />
        </div>

        <div className="space-y-4">
          {normalized.estados.map((estado) => (
            <section
              key={estado.id}
              className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
            >
              <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-sm font-bold text-white">
                    {estado.estado}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-950">
                      Protestos em {estado.estado}
                    </div>
                    <div className="text-xs text-slate-500">
                      {estado.cartorios.length} cidade
                      {estado.cartorios.length === 1 ? "" : "s"} com protesto
                      {estado.cartorios.length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge color="failure">
                    {estado.numeroProtestos} protesto
                    {estado.numeroProtestos === 1 ? "" : "s"}
                  </Badge>
                  <Badge color="gray">
                    {formatCurrency(estado.valorTotal)}
                  </Badge>
                </div>
              </div>

              <div className="divide-y divide-slate-100">
                {estado.cartorios.map((cartorio) => (
                  <div key={cartorio.id} className="px-4 py-4">
                    <div className="flex gap-3 flex-row items-start justify-between">
                      <div className="min-w-0 flex items-center gap-3 justify-between w-full">
                        <div className="flex flex-wrap items-center gap-2">
                          <HiOutlineLocationMarker className="text-lg text-red-700" />
                          <h4 className="break-words text-base font-semibold text-slate-950">
                            {cartorio.cidade}
                          </h4>
                        </div>
                        <div className=" flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 font-semibold">
                          {cartorio.nome && (
                            <span>Cartório: {cartorio.nome}</span>
                          )}
                          <span>
                            {cartorio.numeroProtestos} protesto
                            {cartorio.numeroProtestos === 1 ? "" : "s"}
                          </span>
                          <span>{formatCurrency(cartorio.valorTotal)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
                      <div className="hidden grid-cols-[1fr_180px] gap-3 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:grid">
                        <div className="flex items-center gap-2">
                          <HiOutlineCalendar className="text-base" />
                          Data do protesto
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          <HiOutlineCash className="text-base" />
                          Valor protestado
                        </div>
                      </div>

                      <TitulosCartorio titulos={cartorio.titulos} />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="text-center text-sm text-slate-500">
          Exibindo {normalized.totalTitulos} título
          {normalized.totalTitulos === 1 ? "" : "s"} em{" "}
          {normalized.totalCartorios} cidade
          {normalized.totalCartorios === 1 ? "" : "s"}.
        </div>
      </div>
    </div>
  );
};

export default ProtestosDirCartorio;
